'use client'

import { useEffect, useMemo, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

import { faFilter } from '@document-crunch/pro-regular-svg-icons'

import { BasicTeam } from '@dc-shared-web/hooks/api/team'
import { useGetSolutionTypes } from '@dc-shared-web/hooks/api/solutionType'

import { useAuth } from '@dc-shared-web/providers/AuthProvider'

import { Icon } from '@dc-shared-web/components/base/Icon'
import { Combobox } from '@dc-shared-web/components/base/ComboBox'

import { Button } from '@dc-shared-web/components/shadcn/button'
import { BaseDialog } from '@dc-shared-web/components/base/BaseDialog'
import { Input } from '@dc-shared-web/components/shadcn/input'
import { Label } from '@dc-shared-web/components/shadcn/label'

import { crunchDateFilters } from '@dc-shared-web/lib/helpers'
import { Tooltip, useFSTrackEvent } from '@/dc-shared-web/src'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'
import { TeamPicker } from '@/app/core/components/TeamPicker'
import { useGetDocumentTypes } from '@/app/search/hooks/useGetDocumentTypes'
import { formatDocumentTypes } from '@/app/search/utils/formatDocumentTypes'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'

type PropTypes = {
  teams: BasicTeam[]
}

const crunchCreatedDates = Array.from(crunchDateFilters.keys()).map((val) => ({
  value: val,
  label: val,
}))

export function SearchFilterDialog({ teams }: PropTypes) {
  const { user } = useAuth()
  const router = useRouter()
  const searchParams = useSearchParams()

  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState<string | undefined>('')
  const [team, setTeam] = useState<BasicTeam>()
  const [solutionTypeId, setSolutionTypeId] = useState<string | undefined>('')
  const [documentTypeUuid, setDocumentTypeUuid] = useState<string | undefined>(
    ''
  )
  const [dateRangeId, setDateRangeId] = useState<string | undefined>('')

  const { trackEvent } = useFSTrackEvent()

  const {
    flags: { pd2UploadFlow10 },
  } = useLDFlags()

  const { data: solutionTypes } = useGetSolutionTypes({
    team: team?.text || '',
  })

  const remoteDocumentTypes = useGetDocumentTypes()

  const documentTypes = useMemo(() => {
    if (!remoteDocumentTypes) return []

    return formatDocumentTypes(remoteDocumentTypes)
  }, [remoteDocumentTypes])

  const sections = [
    {
      label: 'Document Types',
      options: documentTypes,
    },
    {
      label: 'Solution Types',
      options: (solutionTypes || []).map((solutionType, i) => ({
        value: i.toString(),
        label: solutionType.type,
      })),
    },
  ]

  const handleSolutionTypeChange = (value: string | undefined) => {
    if (!value) {
      setSolutionTypeId('')
      setDocumentTypeUuid('')
      return
    }

    if (documentTypes?.some((documentType) => documentType.value === value)) {
      setDocumentTypeUuid(value)
      setSolutionTypeId('')
    } else {
      setSolutionTypeId(value)
      setDocumentTypeUuid('')
    }
  }

  useEffect(() => {
    setFilterState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teams, searchParams])

  useEffect(() => {
    if (open) {
      trackEvent(FS.FILTER_DIALOG_OPEN)
    }
  }, [open, trackEvent])

  const setFilterState = () => {
    const searchParam = searchParams.get('search')
    const teamIdParam = searchParams.get('selectedTeamId')
    const solutionTypeParam = searchParams.get('solutionType')
    const documentTypeNameParam = searchParams.get('documentTypeName')
    const crunchDateParam = searchParams.get('crunchDate')

    if (searchParam) {
      setSearchTerm(searchParam)
    } else {
      setSearchTerm('')
    }

    if (teamIdParam) {
      const team = teams.find((team) => teamIdParam === team.uuid)

      if (team) {
        setTeam(team)
      }
    } else {
      setTeam(undefined)
    }

    if (solutionTypeParam) {
      setSolutionTypeId(
        solutionTypes
          ?.findIndex((st) => solutionTypeParam === st.type)
          .toString()
      )
    } else if (documentTypeNameParam) {
      setDocumentTypeUuid(
        documentTypes
          ?.findIndex((dt) => documentTypeNameParam === dt.value)
          .toString()
      )
    } else {
      setSolutionTypeId('')
    }

    if (crunchDateParam) {
      setDateRangeId(
        crunchCreatedDates
          .findIndex((date) => crunchDateParam === date.label)
          .toString()
      )
    } else {
      setDateRangeId('')
    }
  }

  const trackSearch = () => {
    trackEvent(FS.FILTER_DIALOG_SEARCH, {
      search: searchTerm || '',
      team: team?.text || '',
      solutionType: solutionTypeId
        ? solutionTypes?.[+solutionTypeId]?.type || ''
        : '',
      documentType: documentTypeUuid
        ? documentTypes?.[+documentTypeUuid]?.label || ''
        : '',
      crunchDate: dateRangeId
        ? crunchCreatedDates[+dateRangeId]?.label || ''
        : '',
    })
  }

  const handleSearch = () => {
    trackSearch()
    const params = new URLSearchParams(searchParams)
    if (searchTerm) {
      params.set('search', searchTerm)
    } else {
      params.delete('search')
    }

    if (team) {
      params.set('team', team?.text || '')
    } else {
      params.delete('team')
    }

    if (solutionTypeId && solutionTypes) {
      params.set('solutionType', solutionTypes[+solutionTypeId].type)
    } else {
      params.delete('solutionType')
    }

    if (documentTypeUuid && documentTypes) {
      params.set(
        'documentTypeName',
        documentTypes?.find(
          (documentType) => documentType.value === documentTypeUuid
        )?.label || ''
      )
    } else {
      params.delete('documentTypeName')
    }

    if (dateRangeId) {
      params.set('crunchDate', crunchCreatedDates[+dateRangeId].label)
    } else {
      params.delete('crunchDate')
    }

    router.push(`/search?${params}`)
    handleClose()
  }

  const handleClose = () => {
    if (!open) return
    setOpen(false)
    setFilterState()
  }

  return (
    <>
      <Tooltip content='Filter' sideOffset={-2} withArrow>
        <Button
          variant='outline'
          size='icon'
          className='tw-ml-2 tw-w-9 tw-h-9 tw-border-neutral-200'
          onClick={() => setOpen(!open)}
        >
          <Icon icon={faFilter} className='tw-h-5' />
        </Button>
      </Tooltip>

      <BaseDialog
        title='Filter by'
        isOpen={open}
        onClose={handleClose}
        primaryActionSlot={<Button onClick={handleSearch}>Search</Button>}
      >
        <div className='tw-space-y-4'>
          <div>
            <Label>Search term</Label>
            <Input
              placeholder='ex. final_contract.pdf'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              clearInputEnabled
            />
          </div>

          {!user._isUnaffiliatedUser && (
            <div>
              <Label>Team</Label>
              <TeamPicker
                onChange={setTeam}
                value={team}
                showAllTeamsOption={user.is_super_admin}
              />
            </div>
          )}

          <div>
            <Label>Document or Solution type</Label>
            <Combobox
              placeholder='Select type'
              selectedId={solutionTypeId || documentTypeUuid}
              widthClass='tw-w-full'
              options={(!pd2UploadFlow10 && sections[1].options) || []}
              sections={pd2UploadFlow10 && sections}
              onChange={handleSolutionTypeChange}
            />
          </div>

          <div>
            <Label>Crunch date</Label>
            <Combobox
              placeholder='Select a date range'
              selectedId={dateRangeId}
              widthClass='tw-w-full'
              options={crunchCreatedDates.map((date, i) => ({
                value: i.toString(),
                label: date.label,
              }))}
              onChange={setDateRangeId}
            />
          </div>
        </div>
      </BaseDialog>
    </>
  )
}
